import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { formatMoney } from '../../util/currency';
import { richText } from '../../util/richText';
import { isBookingProcessAlias } from '../../transactions/transaction';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl, currencyOptions) => {
  if (price && price.currency === currency) {
    const { displayCurrency, convertToDisplayCurrency } = currencyOptions || {};
    const formattedBasePrice = formatMoney(intl, price);

    // The buyer may browse in another currency than the marketplace currency.
    // The converted price is an estimate: the transaction still happens in the
    // marketplace currency, which is why it's kept visible next to it.
    const shouldConvert = !!displayCurrency && displayCurrency !== price.currency;
    const convertedPrice = shouldConvert ? convertToDisplayCurrency?.(price) : null;

    if (!convertedPrice) {
      return { formattedPrice: formattedBasePrice, priceTooltip: formattedBasePrice };
    }

    const formattedConvertedPrice = formatMoney(intl, convertedPrice);
    return {
      formattedPrice: formattedConvertedPrice,
      formattedBasePrice,
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.convertedPriceTitle' },
        { convertedPrice: formattedConvertedPrice, price: formattedBasePrice }
      ),
    };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @param {Object} [currencyOptions] - display currency info (e.g. from useCurrency())
 * @param {string} [currencyOptions.displayCurrency] - currency the prices are shown in
 * @param {Function} [currencyOptions.convertToDisplayCurrency] - Money -> Money in displayCurrency
 * @returns {Object} translations and derived values:
 *   - titlePlain: raw title string (for aria/alt)
 *   - titleFormatted: React nodes from richText(title) for display
 *   - showPrice: whether to show the price block
 *   - priceTooltip: string for the price element's title attribute (tooltip on hover)
 *   - priceMessage: string or null for the price block content (same translation as used in cardAriaLabel when shown)
 *   - basePriceMessage: string or null for the marketplace currency price, shown when the price is converted
 *   - cardAriaLabel: ready-to-use aria-label for the card link (listing title + price line when shown)
 *   - authorName: "ListingCard.author" string containing author's display name
 */
export const getListingCardTranslations = (listing, config, intl, currencyOptions) => {
  const { title = '', price, publicData } = listing?.attributes || {};

  const authorDisplayName = listing?.author?.attributes?.profile?.displayName;
  const authorName = intl.formatMessage(
    { id: 'ListingCard.author' },
    { authorName: authorDisplayName }
  );

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);
  const { formattedPrice, formattedBasePrice, priceTooltip } = priceData(
    price,
    config.currency,
    intl,
    currencyOptions
  );

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  const priceMessageId = hasMultiplePriceVariants
    ? 'ListingCard.priceStartingFrom'
    : 'ListingCard.price';

  const perUnitString = isBookable
    ? intl.formatMessage({ id: 'ListingCard.perUnit' }, { unitType: publicData?.unitType })
    : '';

  // Single formatted price line (amount + per-unit if applicable); used for both card aria and price block
  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? <span className={css.perUnit}>{perUnitString}</span> : '';
  const priceMessage =
    showPrice && formattedPrice != null
      ? intl.formatMessage({ id: priceMessageId }, { priceValue, pricePerUnit })
      : '';

  // Shown under the converted price, so the buyer sees the currency they'll be charged in.
  const basePriceMessage =
    showPrice && formattedBasePrice != null
      ? intl.formatMessage({ id: 'ListingCard.basePrice' }, { price: formattedBasePrice })
      : null;

  const cardAriaLabel =
    priceMessage.length > 0
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: title, formattedPrice: priceMessage }
        )
      : title;

  return {
    titlePlain: title,
    titleFormatted: richText(title, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    authorName,
    showPrice,
    priceTooltip,
    priceMessage,
    basePriceMessage,
    cardAriaLabel,
  };
};
